import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { LINK_TYPE_META, getLinkMeta } from "./linkMeta";

/**
 * Add a link to one scope. The type list is whatever that scope accepts, so a
 * VEXOS section can only take a VEXOS link and a video section leads with Vimeo
 * — no one has to decide what "media type" means.
 */
export default function AddLinkDialog({ open, scope, onClose, onSubmit }) {
  const [linkType, setLinkType] = useState("");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && scope) {
      setLinkType(scope.linkTypes[0]);
      setTitle("");
      setUrl("");
      setSaving(false);
    }
  }, [open, scope]);

  if (!scope) return null;

  const submit = async () => {
    if (!url.trim()) return;
    setSaving(true);
    const ok = await onSubmit({
      media_type: linkType,
      scope: scope.key,
      title: title.trim() || getLinkMeta(linkType).label,
      url: url.trim(),
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="add-link-dialog">
        <DialogHeader>
          <DialogTitle>Add to {scope.label}</DialogTitle>
          <DialogDescription>{scope.blurb}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {scope.linkTypes.length > 1 && (
            <div className="space-y-2">
              <Label>Kind</Label>
              <Select value={linkType} onValueChange={setLinkType}>
                <SelectTrigger data-testid="add-link-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {scope.linkTypes.map(t => (
                    <SelectItem key={t} value={t}>
                      {LINK_TYPE_META[t]?.addLabel || t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="add-link-url"
            />
          </div>

          <div className="space-y-2">
            <Label>Label <span className="text-muted-foreground font-normal">— optional</span></Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={placeholderFor(linkType, scope)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="add-link-title"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving || !url.trim()} className="rounded-full" data-testid="add-link-submit">
            {saving ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function placeholderFor(linkType, scope) {
  if (linkType === "video_link") return "e.g. Rooftop animation — 60s";
  if (linkType === "doc_link") return "e.g. Investor deck";
  if (linkType === "web_link") return scope.key === "website" ? "e.g. Live site" : "e.g. Landing page";
  if (linkType === "vexos_link") return "e.g. VEXOS platform";
  return "e.g. Final delivery folder";
}
